import { Link as RouterLink, useLocation } from 'react-router'

import Box from '@mui/material/Box'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

import useFormatMessage from '@hooks/useFormatMessage'
import useIsExpanded from '@hooks/useIsExpanded'
import { glassChrome, navCapsule } from '@utils/StylesHelper'

import NavBadge from '../NavBadge'
import { isMainRoute, isNavItemActive, useNavigationItems } from '../navigation'

export const TAB_BAR_HEIGHT = 58

/** How far the bar floats from the edges of the screen. */
const INSET = 12
/** What a page has to keep clear at the bottom so the bar never lands on its content. */
const CLEARANCE = TAB_BAR_HEIGHT + INSET * 2

// The selection is drawn once and moved rather than once per tab, so it has to know
// where the icons sit. Everything below is measured from these, including the tabs.
const PILL_WIDTH = 44
const PILL_HEIGHT = 26
const LABEL_HEIGHT = 12
const LABEL_GAP = 4
const ICON_ROW_TOP = (TAB_BAR_HEIGHT - (PILL_HEIGHT + LABEL_GAP + LABEL_HEIGHT)) / 2

/**
 * Whether the tab bar is on screen: it belongs to the compact shell, and within it to
 * the primary destinations. Detail pages take over the whole screen.
 *
 * @returns {boolean} True when the tab bar should be visible.
 */
function useTabBarVisible() {
  const { pathname } = useLocation()
  const expanded = useIsExpanded()

  return !expanded && isMainRoute(pathname)
}

/**
 * The room a scrolling surface has to leave at its bottom so the floating bar never
 * lands on its last row.
 *
 * A page that scrolls a pane of its own has to put this on the pane rather than around
 * it — padding the shell would end the pane above the bar and leave it in a band.
 *
 * @returns {string|number} A CSS length, or 0 when there is no bar on screen.
 */
export function useTabBarClearance() {
  return useTabBarVisible() ? `calc(${CLEARANCE}px + env(safe-area-inset-bottom))` : 0
}

/**
 * Bottom tab bar, mirroring the four tabs in the mobile app.
 *
 * A capsule floating clear of the bottom edge rather than a strip fixed to it, so the
 * page carries on underneath and is visible through it. Stays mounted at all times so
 * it can animate out of view on detail pages. Renders nothing inside the native shell,
 * which draws its own tabs.
 *
 * @returns {JSX.Element|null} The rendered tab bar.
 */
export default function TabBar() {
  const { pathname } = useLocation()
  const formatMessage = useFormatMessage()
  const visible = useTabBarVisible()

  const tabs = useNavigationItems()
  const activeIndex = tabs.findIndex(({ to }) => isNavItemActive(pathname, to))

  if (window.isNativeApp === true) return null

  return (
    <Box sx={styles.root(visible)}>
      <Box component="nav" aria-label={formatMessage('navigation.tabs.a11y.label')} sx={styles.bar(tabs.length)}>
        {activeIndex >= 0 && <Box aria-hidden="true" sx={styles.selection(activeIndex, tabs.length)} />}

        {tabs.map(({ id, to, label, a11yLabel, Icon, IconActive, count, badgeColor }, index) => {
          const active = index === activeIndex
          const TabIcon = active ? IconActive : Icon

          return (
            <Box
              id={id}
              key={to}
              component={RouterLink}
              to={to}
              aria-current={active ? 'page' : undefined}
              aria-label={a11yLabel}
              data-testid={`tab-${label}`}
              sx={styles.tab(active)}
            >
              <Box sx={styles.iconWrapper}>
                <TabIcon fontSize="small" aria-hidden="true" />
                <NavBadge count={count} color={badgeColor} sx={styles.badge} testId={`nav-badge-${label}`} />
              </Box>
              <Typography variant="caption" noWrap sx={styles.label}>
                {label}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

const styles = {
  // Only the capsule takes the pointer, so the page stays usable either side of it.
  root: (visible) => ({
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: (theme) => theme.zIndex.appBar,
    px: `${INSET}px`,
    pb: `calc(${INSET}px + env(safe-area-inset-bottom))`,
    pointerEvents: 'none',
    // Past its own height, so the shadow clears the screen with it
    transform: visible ? 'translateY(0)' : 'translateY(calc(100% + 16px))',
    visibility: visible ? 'visible' : 'hidden',
    // Hiding is quicker than showing: leaving should feel fast, arriving settled.
    // Visibility is delayed on hide so the transform finishes before the bar stops
    // being focusable.
    transition: visible
      ? 'transform 240ms cubic-bezier(0.2, 0, 0, 1), visibility 0s'
      : 'transform 180ms cubic-bezier(0.4, 0, 1, 1), visibility 0s 180ms',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  }),
  bar: (count) => (theme) => ({
    ...glassChrome(theme),
    position: 'relative',
    pointerEvents: 'auto',
    maxWidth: theme.breakpoints.values.sm,
    mx: 'auto',
    height: TAB_BAR_HEIGHT,
    borderRadius: `${TAB_BAR_HEIGHT / 2}px`,
    px: 0.5,
    display: 'grid',
    gridTemplateColumns: `repeat(${count}, 1fr)`,
  }),
  // One capsule for the whole bar, slid to the tab you are on, so the change reads as
  // the selection travelling rather than the colour moving house.
  selection: (index, count) => ({
    position: 'absolute',
    top: ICON_ROW_TOP,
    // Lines up with the grid inside the bar's own padding, which absolute positioning
    // does not account for on its own
    left: 4,
    width: `calc((100% - 8px) / ${count})`,
    height: PILL_HEIGHT,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    transform: `translateX(${index * 100}%)`,
    transition: 'transform 400ms cubic-bezier(0.22, 1, 0.36, 1)',
    '&::before': {
      content: '""',
      width: PILL_WIDTH,
      borderRadius: 100,
      backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.14),
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  }),
  tab: (active) => ({
    ...navCapsule(active),
    // Above the selection, which is positioned and would otherwise paint over it
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: `${LABEL_GAP}px`,
    minWidth: 0,
    // The capsule behind the icon is the selection, so the tab itself stays clear
    '&:hover': { backgroundColor: 'transparent' },
  }),
  iconWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: PILL_WIDTH,
    height: PILL_HEIGHT,
  },
  label: { fontWeight: 'bold', lineHeight: `${LABEL_HEIGHT}px` },
  badge: {
    position: 'absolute',
    top: -4,
    right: 0,
    minWidth: 17,
    height: 17,
    // Holds the count off the icon it is sitting on
    boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.background.paper, 0.8)}`,
  },
}
